import { type FC } from 'react';
import { useFormik } from 'formik';
import { Button, useAlert } from '@selfinspectionorg/ui-kit';
import { validationSchema, type ChangePasswordFormValues } from './form';
import styles from './ChangePasswordForm.module.scss';
import { PasswordInput } from '@/components/form/PasswordInput';
import { PasswordRequirements } from '@/components/form/PasswordRequirements';
import { changePassword } from '@/shared/api/user';
import { getApiErrorString } from '@/shared/utils';

/**
 * Self-service password change on the Profile Security tab. The user proves identity with their current
 * password and sets a new one (with a matching confirmation). New-password rules and the live checklist
 * come from the shared policy via `PasswordRequirements` / `buildNewPasswordRule` — no hard-coded rules.
 * The change is session-authorized (`PUT /users/password`) and issues no new session. On success the
 * form clears and a success alert is shown; on failure (including a rejected current password) an error
 * alert is shown and the user can retry.
 */
export const ChangePasswordForm: FC = () => {
  const alert = useAlert();

  const form = useFormik<ChangePasswordFormValues>({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      try {
        await changePassword({ oldPassword: values.oldPassword, newPassword: values.newPassword });
        resetForm();
        alert.success('Password changed', undefined, {
          showCloseButton: true,
          showIcon: true,
          testId: 'change-password-success',
        });
      } catch (caught) {
        alert.error("Couldn't change password", getApiErrorString(caught), {
          showCloseButton: true,
          showIcon: true,
          testId: 'change-password-error',
        });
      }
    },
  });

  const isSubmitDisabled = !form.dirty || !form.isValid || form.isSubmitting;

  return (
    <form className={styles.form} onSubmit={form.handleSubmit}>
      {/* Two columns per the Figma: the password fields on the left, the live requirements checklist
          on the right. Collapses to a single column on narrow viewports. */}
      <div className={styles.columns}>
        <div className={styles.fields}>
          <PasswordInput
            name="oldPassword"
            label="Current password"
            placeholder="Enter current password"
            autoComplete="current-password"
            value={form.values.oldPassword}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            errored={!!form.touched.oldPassword && !!form.errors.oldPassword}
            helperMessage={form.touched.oldPassword ? form.errors.oldPassword : undefined}
            required
            testId="current-password-input"
          />
          <PasswordInput
            name="newPassword"
            label="New password"
            placeholder="Enter new password"
            autoComplete="new-password"
            value={form.values.newPassword}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            errored={!!form.touched.newPassword && !!form.errors.newPassword}
            required
            testId="new-password-input"
          />
          <PasswordInput
            name="confirmPassword"
            label="Confirm password"
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={form.values.confirmPassword}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            errored={!!form.touched.confirmPassword && !!form.errors.confirmPassword}
            helperMessage={form.touched.confirmPassword ? form.errors.confirmPassword : undefined}
            required
            testId="confirm-password-input"
          />
        </div>

        <div className={styles.rules}>
          <span className={styles.rulesTitle}>New password must contain</span>
          <PasswordRequirements value={form.values.newPassword} />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          type="submit"
          width="100%"
          isLoading={form.isSubmitting}
          disabled={isSubmitDisabled}
          data-testid="change-password-button"
        >
          Change password
        </Button>
      </div>
    </form>
  );
};
